import * as React from "react";
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Bell,
  Zap,
  Bug,
  Calendar,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { changelogEntries, ChangelogEntry } from "./changelog-data";

interface TranslatedEntry {
  id: string;
  title: string;
  description: string;
}

interface ChangelogProps {
  className?: string;
  user?: { email: string | null } | null;
}

const Changelog: React.FC<ChangelogProps> = ({ className = "", user }) => {
  const { t, i18n } = useTranslation("home");

  // Get translated entries from changelog namespace
  const changelogData = i18n.getResourceBundle(i18n.language, "changelog") as
    | { entries?: TranslatedEntry[] }
    | undefined;
  const translatedEntries = changelogData?.entries || [];

  // Merge translations with metadata from original entries
  const entries: ChangelogEntry[] = changelogEntries.map((entry) => {
    const translated = translatedEntries.find((te) => te.id === entry.id);
    return {
      ...entry,
      title: translated?.title || entry.title,
      description: translated?.description || entry.description,
    };
  });
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Show latest 1 entry by default, all when expanded
  const displayEntries = isExpanded ? entries : entries.slice(0, 1);

  const getEntryIcon = (type: ChangelogEntry["type"]) => {
    switch (type) {
      case "book_update":
        return <BookOpen className="w-4 h-4" />;
      case "general_news":
        return <Bell className="w-4 h-4" />;
      case "feature":
        return <Zap className="w-4 h-4" />;
      case "bug_fix":
        return <Bug className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const getEntryIconColor = (type: ChangelogEntry["type"]) => {
    switch (type) {
      case "book_update":
        return "text-accent";
      case "general_news":
        return "text-blue-500";
      case "feature":
        return "text-green-500";
      case "bug_fix":
        return "text-orange-500";
      default:
        return "text-secondary";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return t("changelog.today");
    if (diffDays === 2) return t("changelog.yesterday");
    if (diffDays <= 7) return t("changelog.daysAgo", { count: diffDays - 1 });

    // Use locale-aware date formatting
    const localeMap: Record<string, string> = {
      en: "en-US",
      fr: "fr-FR",
      de: "de-DE",
      es: "es-ES",
      zh: "zh-CN",
      ja: "ja-JP",
    };
    const locale = localeMap[i18n.language] || "en-US";

    return date.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const isNewEntry = (entry: ChangelogEntry) => {
    if (entry.isNew) return true;

    // Auto-detect new entries (last 7 days)
    const entryDate = new Date(entry.date);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return entryDate > sevenDaysAgo;
  };

  if (!entries || entries.length === 0) {
    return null;
  }

  return (
    <div
      className={`bg-secondary/30 border border-tertiary rounded-lg p-4 overflow-hidden ${className}`}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left hover:bg-secondary/20 -mx-4 -mt-4 -mb-2 p-4 rounded-lg transition-colors"
        aria-label={isExpanded ? "Collapse changelog" : "Expand changelog"}
      >
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-primary">
              {t("changelog.whatsNew")}
            </span>
            <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full font-medium">
              {entries.length}{" "}
              {entries.length === 1
                ? t("changelog.update")
                : t("changelog.updates")}
            </span>
          </div>
        </div>
        <div className="ml-2 text-secondary hover:text-primary transition-colors">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      <div className="mt-4 pt-4 border-t border-tertiary">
        <div
          className={`space-y-4 overflow-hidden ${
            isExpanded ? "max-h-96 overflow-y-auto scrollbar-styled" : ""
          }`}
        >
          {displayEntries.map((entry) => (
            <div
              key={entry.id}
              className="flex gap-3 group hover:bg-secondary/20 px-2 py-2 rounded-lg transition-colors overflow-hidden"
            >
              <div
                className={`flex-shrink-0 mt-0.5 ${getEntryIconColor(
                  entry.type
                )}`}
              >
                {getEntryIcon(entry.type)}
              </div>

              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-sm font-medium text-primary leading-snug break-words">
                    {entry.title}
                  </h4>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {entry.bookRevision && (
                      <span
                        className="border border-tertiary text-secondary text-xs px-2 py-0.5 rounded-full font-mono"
                        title={t("changelog.bookRevisionHint")}
                      >
                        {entry.bookRevision}
                      </span>
                    )}
                    {isNewEntry(entry) && (
                      <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        {t("changelog.new")}
                      </span>
                    )}
                    <span className="text-xs text-secondary whitespace-nowrap">
                      {formatDate(entry.date)}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-secondary leading-relaxed break-words overflow-wrap-anywhere">
                  {entry.description}
                  {entry.requiresSignIn && !user && (
                    <span className="text-accent font-medium">
                      {" "}
                      - {t("changelog.signInToAccess")}
                    </span>
                  )}
                </p>
                {entry.ctaUrl && (
                  <a
                    href={entry.ctaUrl}
                    className="inline-block mt-1 text-sm text-accent hover:text-accent/80 font-medium transition-colors"
                  >
                    {t(entry.ctaLabelKey ?? "announcement.open")} →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="pt-2 border-t border-tertiary flex items-center justify-between gap-3 flex-wrap">
          {entries.length > 1 ? (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-accent hover:text-accent/80 font-medium transition-colors"
            >
              {isExpanded
                ? t("changelog.showLess")
                : entries.length - 1 === 1
                  ? t("changelog.showMore", { count: entries.length - 1 })
                  : t("changelog.showMorePlural", { count: entries.length - 1 })}
            </button>
          ) : (
            <span />
          )}

          {/* Absolute: this card also renders on buy.multiagentbook.com, and
              /changelog is the canonical public page printed inside the book. */}
          <a
            href="https://multiagentbook.com/changelog"
            className="text-sm text-secondary hover:text-primary transition-colors"
          >
            {t("changelog.viewAll")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Changelog;
